import get from 'lodash/get';
import { AggregationData } from '../../../@types';
import { parseDataValue } from '../../../utils/dataUtils';

/**
 * Exported interfaces/types
 */

export interface AggregateObj {
  type: NonNullable<AggregationData['operation']>;
  sum: number;
  count?: number;
  max?: number;
  min?: number;
  /** A key reference to replace min or max values */
  ref?: string;
  /** The referenced key value for min or max operations */
  refValue?: string | number;
  /** An array of items to which perform a distinct count or mode */
  items?: unknown[];
}

//
// First-pass and second-pass aggregation
//

type FirstPassAggregateFunction = (
  value: Partial<AggregateObj>,
) => AggregateObj;

/**
 * Returns the most frequent number of an array of numbers.
 */
export const mode = (arr: number[]): number => {
  const frequencyMap = arr.reduce<Record<number, number>>((acc, curr) => {
    acc[curr] = (acc[curr] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(frequencyMap).reduce((acc, [key, value]) => {
    return value > frequencyMap[acc] ? Number(key) : acc;
  }, 0);
};

/**
 * Exports a function to perform the first-pass of aggregation
 * Note: You should be calling this while processing each data value
 */
export const firstPassAggregateFunction = (
  def: AggregationData,
  data: Record<string, unknown>,
): FirstPassAggregateFunction => {
  const value = parseDataValue(data, def.key, def.dataType || 'integer') || 0;

  switch (def.operation) {
    case 'percentage':
      return (prev: Partial<AggregateObj>) => ({
        type: prev.type || def.operation || 'percentage',
        sum:
          typeof value === 'number' ? (prev.sum || 0) + value : prev.sum || 0,
      });

    case 'avg':
    case 'weighted-avg': {
      const weight = def.weightBy
        ? parseDataValue(data, def.weightBy, 'integer') || 1
        : 1;
      return (prev: Partial<AggregateObj>) => ({
        type: prev.type || def.operation || 'avg',
        sum: (prev.sum || 0) + (value as number) * weight,
        count: (prev.count || 0) + weight,
      });
    }

    case 'max': {
      const refValue = def.ref
        ? (parseDataValue(data, def.ref, def.refDataType || 'string') as
            | string
            | number)
        : undefined;

      return (prev: Partial<AggregateObj>) => {
        const previousValue =
          prev.max === undefined || Number.isNaN(prev.max)
            ? Number.MIN_SAFE_INTEGER
            : prev.max;
        const currentValue = value as number;

        return {
          type: prev.type || def.operation || 'max',
          sum: 0,
          max: previousValue > currentValue ? previousValue : currentValue,
          ref: def.ref,
          refValue: previousValue > currentValue ? prev.refValue : refValue,
        };
      };
    }

    case 'min': {
      const refValue = def.ref
        ? (parseDataValue(data, def.ref, def.refDataType || 'string') as
            | string
            | number)
        : undefined;
      const currentValue = value as number;

      return (prev: Partial<AggregateObj>) => {
        const previousValue =
          prev.min === undefined || Number.isNaN(prev.min)
            ? Number.MAX_SAFE_INTEGER
            : prev.min;
        return {
          type: prev.type || def.operation || 'min',
          sum: 0,
          min: previousValue < currentValue ? previousValue : currentValue,
          ref: def.ref,
          refValue: previousValue < currentValue ? prev.refValue : refValue,
        };
      };
    }

    case 'mode':
    case 'distinctCount':
      return (prev: Partial<AggregateObj>) => ({
        type: prev.type || def.operation || 'distinctCount',
        sum: 0,
        items: prev.items ? [...prev.items, value] : [value],
      });

    case 'sum':
    default:
      return (prev: Partial<AggregateObj>) => ({
        type: prev.type || def.operation || 'sum',
        sum:
          typeof value === 'number' ? (prev.sum || 0) + value : prev.sum || 0,
      });
  }
};

/**
 * Performs the second-pass of aggregation
 * Note: You should be calling this during the post-processing
 * @param def
 * @returns
 */
export const secondPassAggregateFunction = (
  prev: AggregateObj,
  entries: Array<AggregateObj> = [],
): number => {
  switch (prev.type) {
    case 'percentage': {
      const total = entries.reduce((acc, v) => acc + v.sum, 0);
      return prev.sum / (total || 1);
    }

    case 'avg':
    case 'weighted-avg':
      return prev.sum / (prev.count || 1);

    case 'max':
      return prev.max || 0;

    case 'min':
      return prev.min || 0;

    case 'distinctCount':
      return new Set(prev.items as []).size;

    case 'mode':
      return mode(prev.items as []);

    case 'sum':
    default:
      return prev.sum;
  }
};

//
// Footer aggregation
//

/**
 * Aggregates data in a table footer.
 *
 * @param rows
 * @param key
 * @param def
 * @returns
 */
export const aggregateFooter = (
  rows: Array<Record<string, number>>,
  key: string,
  def: AggregationData & { footerWeightKey?: string },
): number => {
  const aux = { sum: 0, count: 0 };

  rows
    .map((row) => ({
      page: row,
      value: row[key],
    }))
    .forEach((row) => {
      // Skip non-numeric placeholder cells (e.g. the '-' surfaced for empty
      // aggregation cells that declare a defaultValue) so they don't poison the
      // footer aggregation.
      if (!Number.isFinite(Number(row.value))) return;

      if (!def.operation || ['sum', 'percentage'].includes(def.operation)) {
        aux.sum = (aux.sum || 0) + row.value;
      } else if (['avg', 'weighted-avg'].includes(def.operation)) {
        // skip average aggregation for 0 lines
        if (def.operation === 'avg' && row.value === 0) return;

        let weight = 1;
        if (def.footerWeightKey) {
          // split keys to adhere to subgroups
          const parts = key.split('-');
          const k1 = parts.length > 1 ? `${parts[0]}-` : '';
          weight = get(row.page, `${k1}${def.footerWeightKey}`, 1);
        }

        // skip average aggregation for 0 lines
        if (def.operation === 'weighted-avg' && weight === 0) return;

        aux.sum = (aux.sum || 0) + row.value * weight;
        aux.count = (aux.count || 0) + weight;
      }
    });

  return def.operation === 'avg' || def.operation === 'weighted-avg'
    ? aux.sum / (aux.count || 1)
    : aux.sum;
};
